import { useState, ChangeEvent, useEffect } from 'react';
import { Download } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { hairstyleService, HairstyleConfig } from '@/services/hairstyleService';
import { creditService } from '@/services/creditService';
import { aiGenerationService } from '@/services/aiGenerationService';
import { useTranslation } from 'react-i18next';

/**
 * AI Hairstyle Generator React App
 *
 * This component provides a UI to upload a photo, enter an optional custom prompt,
 * and generate 6 different hairstyle variations using the Gemini API.
 */
export default function  CreateHairstylePage  () {
  const { user } = useAuth();
  const { t } = useTranslation();
  
  // State to hold the uploaded image data (base64 and MIME type)
  const [uploadedImage, setUploadedImage] = useState<{
    base64: string;
    mimeType: string;
  } | null>(null);


  // State to store the selected hairstyle config
  const [selectedConfig, setSelectedConfig] = useState<HairstyleConfig | null>(null);

  // State to store the generated hairstyle image
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);

  // State to track loading status
  const [isGenerating, setIsGenerating] = useState(false);

  // State for the user's custom text prompt
  const [customPrompt, setCustomPrompt] = useState('');

  // State for user credits and cost
  const [userCredits, setUserCredits] = useState(0);
  const [isLoadingCredits, setIsLoadingCredits] = useState(true);

  // State for hairstyle configurations from database
  const [hairstyleConfigs, setHairstyleConfigs] = useState<(HairstyleConfig & { imageUrl: string })[]>([]);
  const [isLoadingConfigs, setIsLoadingConfigs] = useState(true);

  // Load user credits and hairstyle configs on component mount
  useEffect(() => {
    loadUserCredits();
    loadHairstyleConfigs();
  }, [user]);

  const loadUserCredits = async () => {
    if (!user) {
      setIsLoadingCredits(false);
      return;
    }
    
    try {
      const credits = await creditService.getUserCredits(user.id);
      setUserCredits(credits?.credits || 0);
    } catch (error) {
      console.error('Error loading user credits:', error);
    } finally {
      setIsLoadingCredits(false);
    }
  };

  const loadHairstyleConfigs = async () => {
    try {
      const configs = await hairstyleService.getHairstyleConfigsForDisplay();
      setHairstyleConfigs(configs);
      
      // Set the first config as selected by default
      if (configs.length > 0) {
        setSelectedConfig(configs[0]);
      }
    } catch (error) {
      console.error('Error loading hairstyle configs:', error);
    } finally {
      setIsLoadingConfigs(false);
    }
  };


  /**
   * Handles the file input change event.
   * Reads the selected image file, converts it to a base64 string,
   * and updates the `uploadedImage` state.
   * @param {ChangeEvent<HTMLInputElement>} event - The file input change event.
   */
  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setUploadedImage({
        base64: result,
        mimeType: file.type,
      });
    };
    reader.readAsDataURL(file);
  };

  /**
   * Downloads an image from a data URL
   * @param {string} dataUrl - The data URL of the image to download
   * @param {string} filename - The filename for the downloaded image
   */
  const downloadImage = (dataUrl: string, filename: string) => {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /**
   * Handles the 'Generate' button click.
   * Initiates the hairstyle generation process for the selected hairstyle.
   */
  const handleGenerateClick = async () => {
    if (!user) {
      alert(t('createhairstyle.pleaseLogin'));
      return;
    }
    
    if (!uploadedImage) {
      alert(t('createhairstyle.pleaseUploadPhoto'));
      return;
    }
    
    if (!selectedConfig) {
      alert(t('createhairstyle.pleaseSelectHairstyle'));
      return;
    }
    
    if (userCredits < selectedConfig.credit_cost) {
      alert(t('createhairstyle.insufficientCredits'));
      return;
    }
    
    if (isGenerating) return;

    setIsGenerating(true);
    setGeneratedImage(null); // Clear previous result
    const startTime = Date.now();

    try {
      console.log('🚀 开始发型生成流程...', selectedConfig.name);
      
      // 第一步：创建生成记录
      const generationResult = await hairstyleService.createHairstyleGeneration(
        user.id,
        uploadedImage.base64,
        customPrompt
      );

      if (!generationResult.success || !generationResult.generationId) {
        alert(t('createhairstyle.createGenerationFailed') + ': ' + (generationResult.error || t('createhairstyle.unknownError')));
        setIsGenerating(false);
        return;
      }

      console.log('✅ 生成记录创建成功:', generationResult.generationId);

      // 第二步：直接调用AI生成服务
      console.log('🎨 开始AI图像生成...');
      
      // 构建生成提示词
      const prompt = `${selectedConfig.hairstyle_description} ${selectedConfig.hair_color}${customPrompt ? ', ' + customPrompt : ''}`;
      
      // 调用AI生成服务
      const aiResult = await aiGenerationService.generateHairstyleImage(
        prompt,
        uploadedImage.base64.split(',')[1],
        uploadedImage.mimeType
      );

      if (aiResult.success && aiResult.imageUrl) {
        console.log('✅ AI图像生成成功');
        setGeneratedImage(aiResult.imageUrl);
        
        // 第三步：更新数据库记录为成功
        const endTime = Date.now();
        const processingTime = Math.round((endTime - startTime) / 1000);
        
        await hairstyleService.updateHairstyleGeneration(
          generationResult.generationId,
          selectedConfig.id,
          aiResult.imageUrl,
          'success',
          undefined,
          prompt,
          processingTime
        );
        
        // 扣除积分
        await creditService.consumeCredits(
          user.id, 
          selectedConfig.credit_cost, 
          `AI发型生成: ${selectedConfig.name}`,
          generationResult.generationId,
          'hairstyle_generation'
        );
        
        // 刷新用户积分
        loadUserCredits();
        
        alert(t('createhairstyle.generationComplete'));
        
      } else {
        console.error('❌ AI图像生成失败:', aiResult.error);
        setGeneratedImage('error');
        
        // 更新数据库记录为失败
        await hairstyleService.updateHairstyleGeneration(
          generationResult.generationId,
          selectedConfig.id,
          '',
          'failed',
          aiResult.error
        );
        
        alert(t('createhairstyle.generationFailed'));
      }
      
    } catch (error) {
      console.error('❌ 发型生成过程中发生错误:', error);
      alert(t('createhairstyle.generationError') + ': ' + (error instanceof Error ? error.message : t('createhairstyle.unknownError')));
      setGeneratedImage('error');
    } finally {
      setIsGenerating(false);
    }
  };

  // The component's UI is defined using JSX.
  return (
    <div className="container1 mx-auto">
      <aside className="controls-panel">
        <div className="controls-header">
          <h1>{t('createhairstyle.title')}</h1>
        </div>
        
        {user ? (
          <div className="points-display">
            <i className="fa-solid fa-wallet"></i>
            <span>{t('createhairstyle.currentCredits')}: {userCredits}</span>
          </div>
        ) : (
          <div className="points-display" style={{ backgroundColor: '#fee2e2', color: '#dc2626' }}>
            <i className="fa-solid fa-exclamation-triangle"></i>
            <span>{t('createhairstyle.pleaseLogin')}</span>
          </div>
        )}

        <div className="control-section">
          <h2>{t('createhairstyle.selectHairstyleType')}</h2>
          {isLoadingConfigs ? (
            <div className="loading-state">
              <i className="fa-solid fa-spinner fa-spin"></i>
              <span>{t('createhairstyle.loadingConfigs')}</span>
            </div>
          ) : hairstyleConfigs.length === 0 ? (
            <div className="error-state">
              <i className="fa-solid fa-exclamation-triangle"></i>
              <span>{t('createhairstyle.noConfigsAvailable')}</span>
            </div>
          ) : (
            <div className="hairstyle-selector">
              {hairstyleConfigs.map((config) => (
                <div
                  key={config.id}
                  className={`hairstyle-option ${selectedConfig?.id === config.id ? 'selected' : ''}`}
                  onClick={() => setSelectedConfig(config)}
                >
                  <div className="option-image">
                    <img src={config.imageUrl} alt={config.name} />
                  </div>
                  <div className="option-info">
                    <h3>{config.name}</h3>
                    <p>{config.description}</p>
                    <div className="option-cost">
                      <i className="fa-solid fa-coins"></i>
                      <span>{config.credit_cost} {t('createhairstyle.credits')}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="control-section">
          <h2>{t('createhairstyle.uploadPhoto')}</h2>
          <label htmlFor="file-input" className="upload-area">
            <div
              id="upload-content"
              style={{
                display: uploadedImage ? 'none' : 'flex',
              }}
            >
              <i className="fa-solid fa-arrow-up-from-bracket"></i>
              <p>{t('createhairstyle.clickToUpload')}</p>
              <p className="upload-hint">{t('createhairstyle.uploadHint')}</p>
            </div>
            {uploadedImage && (
              <img
                id="image-preview"
                src={uploadedImage.base64}
                alt="User's photo preview"
                style={{ display: uploadedImage ? 'block' : 'none' }}
              />
            )}
          </label>
          <input
            type="file"
            id="file-input"
            accept="image/png, image/jpeg"
            onChange={handleFileUpload}
          />
        </div>

        <div className="control-section">
          <h2>{t('createhairstyle.customPrompt')}</h2>
          <textarea
            id="custom-prompt"
            placeholder={t('createhairstyle.customPromptPlaceholder')}
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
          ></textarea>
        </div>

        <button
          id="generate-button"
          className="generate-button"
          onClick={handleGenerateClick}
          disabled={isGenerating || !uploadedImage || !user || !selectedConfig || userCredits < (selectedConfig?.credit_cost || 0) || isLoadingCredits || isLoadingConfigs}
        >
          {isGenerating ? (
            <>
              <i className="fa-solid fa-spinner fa-spin"></i>
              <span>{t('createhairstyle.generationInProgress')}</span>
            </>
          ) : !user ? (
            <>
              <i className="fa-solid fa-user-lock"></i>
              <span>{t('createhairstyle.pleaseLoginFirst')}</span>
            </>
          ) : !selectedConfig ? (
            <>
              <i className="fa-solid fa-hand-pointer"></i>
              <span>{t('createhairstyle.pleaseSelectType')}</span>
            </>
          ) : userCredits < selectedConfig.credit_cost ? (
            <>
              <i className="fa-solid fa-coins"></i>
              <span>{t('createhairstyle.insufficientCreditsShort')}</span>
            </>
          ) : isLoadingConfigs ? (
            <>
              <i className="fa-solid fa-spinner fa-spin"></i>
              <span>{t('createhairstyle.loadingCredits')}</span>
            </>
          ) : (
            <>
              <i className="fa-solid fa-wand-magic-sparkles"></i>
              <span>{t('createhairstyle.generateButton')} ({selectedConfig.credit_cost} {t('createhairstyle.credits')})</span>
            </>
          )}
        </button>
      </aside>

      <section className="results-panel">
        <div className="result-container">
          {selectedConfig ? (
            <div className="selected-hairstyle-info">
              <h2>{t('createhairstyle.selectedHairstyle')}: {selectedConfig.name}</h2>
              <p>{selectedConfig.description}</p>
            </div>
          ) : (
            <div className="no-selection">
              <h2>{t('createhairstyle.noSelection')}</h2>
              <p>{t('createhairstyle.noSelectionDesc')}</p>
            </div>
          )}
          
          <div className="generation-result">
            {generatedImage ? (
              generatedImage === 'error' ? (
                <div className="error-state">
                  <i className="fa-solid fa-triangle-exclamation"></i>
                  <h3>{t('createhairstyle.generationFailed')}</h3>
                  <p>{t('createhairstyle.generationError')}</p>
                </div>
              ) : (
                    <div className="generated-image-container">
                      <img
                    src={generatedImage}
                    alt="Generated hairstyle"
                    className="generated-image"
                      />
                  <div className="image-actions">
                      <button
                        className="download-button"
                      onClick={() => downloadImage(generatedImage, `hairstyle-${selectedConfig?.name || 'generated'}-${Date.now()}.png`)}
                        title={t('createhairstyle.downloadImage')}
                      >
                      <Download size={18} />
                      </button>
                    </div>
                </div>
              )
            ) : isGenerating ? (
              <div className="loading-state">
                <div className="loader"></div>
                <h3>{t('createhairstyle.generatingStatus')}</h3>
                <p>{t('createhairstyle.generatingDesc')}</p>
              </div>
            ) : selectedConfig ? (
              <div className="preview-state">
                <img
                  src={(selectedConfig as HairstyleConfig & { imageUrl: string }).imageUrl}
                  alt={selectedConfig.name}
                  className="preview-image"
                />
                <div className="preview-overlay">
                  <span className="preview-label">{t('createhairstyle.exampleEffect')}</span>
                </div>
                <p className="preview-text">{t('createhairstyle.previewText')}</p>
              </div>
            ) : (
              <div className="empty-state">
                <i className="fa-solid fa-hand-pointer"></i>
                <h3>{t('createhairstyle.selectToStart')}</h3>
                <p>{t('createhairstyle.selectToStartDesc')}</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};